import React, { useState, useEffect, useRef } from 'react'
import { useTracking } from 'react-tracking'

import { CoremediaRenderer } from '@ycos/coremedia-renderer'
import BEMHelper from '@ynap/bem-helper'
import { SimpleAsyncButton, Button } from '@ynap/button'
import Checkbox from '@ynap/checkbox'
import EmptyList from '@ynap/empty-list'
import { Notification } from '@ynap/notification'
import { WithReducedSiteFurniture } from '@ynap/with-reduced-site-furniture'

import { MyAccountNavWrapper } from '..'
import { ConsentPreference, ConsentCheckbox, ConsentItem } from '../MarketingPreferences/CoreMediaComponents'
import { ConsentHeader, ConsentFooter, ConsentSuccess } from './CoreMediaComponents'
import UnsubscribeSkeleton from './UnsubscribeSkeleton'
import filterContent from './filterUtils'
import { trackUnsubscribe } from './analytics'

const bem = new BEMHelper('Unsubscribe')

const Unsubscribe = ({
    getLinks,
    userSummary,
    accessKey,
    email,
    validationCode,
    nativeApp,
    country,
    fetchCountry,
    isFetchingCountry,
    fetchCountryError,
    content,
    fetchContent,
    isFetchingContent,
    fetchContentError,
    updateSubscriptions,
    isUpdatingSubscriptions,
    updateSubscriptionsError,
    messages,
    fullPageHeader,
    fetchUser,
}) => {
    const [formValues, setFormValues] = useState({})
    const [allChecked, setAllChecked] = useState(false)
    const [unsubscribeSuccessful, setUnsubscribeSuccessful] = useState(false)
    const [hasUpdateError, setHasUpdateError] = useState(false)

    const submitWrapperRef = useRef()
    const { trackEvent } = useTracking()

    const loading = !!isFetchingCountry || !!isFetchingContent
    const error = !!fetchCountryError || !!fetchContentError
    const guestUser = (accessKey || validationCode) && email
    const accountNavProps = {
        userSummary,
        messages,
        getLinks,
        fullPageHeader,
        title: messages?.pages?.preferences?.title(),
    }
    const components = [ConsentHeader, ConsentPreference, ConsentCheckbox, ConsentItem, ConsentFooter, ConsentSuccess]

    useEffect(() => {
        document.addEventListener('click', handleRemoveError)

        if (typeof content === 'undefined') {
            if (!country && !isFetchingCountry) {
                fetchCountry().then(() => {
                    if (typeof content === 'undefined' || typeof content?.response === 'undefined') {
                        fetchContent('unsubscribe-preferences-collection')
                    }
                })
            } else if ((country && typeof content === 'undefined') || typeof content?.response === 'undefined') {
                fetchContent('unsubscribe-preferences-collection')
            }
        }
    }, [])

    useEffect(() => {
        const marketingOptions = {}

        content?.items.forEach((item) => {
            const itemKey = item.items?.find((child) => child.layoutVariant === 'consent-checkbox').ID
            if (itemKey) marketingOptions[itemKey] = itemKey === 'maMarketingConsent' ? true : false
        })

        setFormValues(marketingOptions)
    }, [content])

    useEffect(() => {
        if (Object.keys(formValues).length && Object.values(formValues).every((value) => value === true)) {
            setAllChecked(true)
        }
    }, [formValues])

    useEffect(() => {
        setHasUpdateError(updateSubscriptionsError)
    }, [updateSubscriptionsError])

    function handleRemoveError(e) {
        if (!submitWrapperRef?.current?.contains(e.target)) {
            setHasUpdateError(false)
        }
    }

    function handleCheck(name) {
        setFormValues({
            ...formValues,
            [name]: !formValues[name],
        })

        if (allChecked) {
            setAllChecked(!allChecked)
        }
    }

    function handleCheckAll() {
        setAllChecked(!allChecked)

        const newFormValues = {}
        for (const [subscription] of Object.entries(formValues)) {
            newFormValues[subscription] = !allChecked
        }
        setFormValues(newFormValues)
    }

    async function handleSubmit() {
        // BE only accepts a 'false' string for the unsubscribe so we send 'false' values for the boxes that are checked
        const brandsToUnsubscribe = {}
        for (const [subscription, checked] of Object.entries(formValues)) {
            if (checked) {
                brandsToUnsubscribe[subscription] = 'false'
            }
            // If maMarketingConsent is not checked, we must send it as 'true' so that BE doesn't treat is as falsy
            if (subscription === 'maMarketingConsent' && !checked) {
                brandsToUnsubscribe[subscription] = 'true'
            }
        }

        await updateSubscriptions(brandsToUnsubscribe, {
            accessKey: accessKey || validationCode,
            email,
        }).then(() => {
            if (!hasUpdateError) {
                trackEvent(trackUnsubscribe())
                setUnsubscribeSuccessful(true)
            }
        })

        if (!guestUser) {
            fetchUser()
        }
    }

    const { brandMarketingConsentItem, otherBrandsConsentItems, footerItem, successItem } = filterContent(content)

    const Form = (
        <div className={bem('wrapper', [nativeApp && 'nativeApp', guestUser && 'guestUser'])}>
            {!unsubscribeSuccessful ? (
                <>
                    {CoremediaRenderer({ items: brandMarketingConsentItem }, components, {
                        rules: [
                            ({ item }) => {
                                if (item.layoutVariant === 'consent-checkbox') {
                                    return {
                                        item,
                                        form: formValues,
                                        modifier: 'unsubscribe',
                                        handleCheck,
                                    }
                                }
                                if (item.layoutVariant === 'consent-preference') {
                                    return { item, modifier: 'unsubscribe' }
                                }
                            },
                        ],
                    })}

                    <details className={bem('brands-collapsible')}>
                        <summary className={bem('brands-collapsible-header')}>{messages.unsubscribe.viewAll()}</summary>
                        <section>
                            {CoremediaRenderer({ items: otherBrandsConsentItems }, components, {
                                rules: [
                                    ({ item }) => {
                                        if (item.layoutVariant === 'consent-checkbox') {
                                            return {
                                                item,
                                                form: formValues,
                                                modifier: 'unsubscribe',
                                                handleCheck,
                                            }
                                        }
                                        if (item.layoutVariant === 'consent-preference') {
                                            return { item, modifier: 'unsubscribe', withLabel: true }
                                        }
                                    },
                                ],
                            })}
                            <Checkbox
                                className={bem('consent-all-checkbox')}
                                type="checkbox"
                                checked={allChecked}
                                onChange={handleCheckAll}
                                id="allBrands"
                            >
                                {messages.unsubscribe.allBrands()}
                            </Checkbox>
                        </section>
                    </details>

                    <div className={bem('consent-button-container')} ref={submitWrapperRef}>
                        {hasUpdateError && (
                            <Notification
                                className={bem('notification', 'bottom')}
                                arrow="bottom"
                                type="error"
                                message={messages.marketing.updateError()}
                            />
                        )}
                        <SimpleAsyncButton
                            type="primary"
                            loading={isUpdatingSubscriptions}
                            className={bem('consent-button')}
                            disabled={Object.values(formValues)?.every((value) => !value)}
                            copy={messages.unsubscribe.unsubscribeBtn()}
                            onClick={handleSubmit}
                        />
                    </div>

                    {CoremediaRenderer({ items: footerItem }, components)}
                </>
            ) : (
                CoremediaRenderer({ items: successItem }, components)
            )}
        </div>
    )

    const ErrorComponent = (
        <EmptyList
            className={bem('empty', [nativeApp && 'nativeApp', guestUser && 'guestUser'])}
            icon={<div className={bem('emptyStateIcon')} />}
            variant={'myAccountError'}
            title={messages.marketingPreferences.pageError()}
        >
            <Button
                onClick={() => {
                    if (fetchCountryError) {
                        fetchCountry().then(() => {
                            fetchContent('unsubscribe-preferences-collection')
                        })
                    }

                    if (fetchContentError) {
                        fetchContent('unsubscribe-preferences-collection')
                    }
                }}
                type="primary-link"
                className={bem('emptyLink')}
            >
                {messages.marketingPreferences.retryNow()}
            </Button>
        </EmptyList>
    )

    const UnsubscribeElements = error ? (
        ErrorComponent
    ) : (loading && !error) || !content ? (
        <UnsubscribeSkeleton guestUser={guestUser} />
    ) : (
        Form
    )

    return nativeApp || guestUser ? (
        <WithReducedSiteFurniture>{UnsubscribeElements}</WithReducedSiteFurniture>
    ) : (
        <MyAccountNavWrapper {...accountNavProps}>{UnsubscribeElements}</MyAccountNavWrapper>
    )
}

export default Unsubscribe
